import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Ruler, ChevronLeft, Building2, Check, DollarSign, Plus, Trash2, Image as ImageIcon, ShoppingCart } from 'lucide-react';
import { getSuppliers, Customer } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';
import { getProjectMeasurements, ProjectMeasurement, getCustomFieldValue, getCustomFieldUnit as getFieldUnit } from '../lib/projectMeasurements';
import { getMeasurementPresets, MeasurementPreset } from '../lib/measurementPresets';
import { getMeasurementPhotos, getMeasurementPhotoUrl, type MeasurementPhoto } from '../services/measurementPhotoService';
import { toast } from './toasts/SimpleToast';
import {
  createOrder,
  OrderItem as DBOrderItem,
  updateOrderEmailInfo,
  linkEmailToOrder,
} from '../lib/purchaseOrders';
import { createStockItem } from '../lib/stockItems';
import OrderSummaryPDFModal from './OrderSummaryPDFModal';

interface PlaceOrderFromMeasurementsModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string | null;
  projectTitle?: string | null;
  onOrderCreated?: () => void;
}

type ModalStep = 'select-supplier' | 'select-measurements' | 'item-details' | 'summary';

export default function PlaceOrderFromMeasurementsModal({
  isOpen,
  onClose,
  projectId,
  projectTitle,
  onOrderCreated
}: PlaceOrderFromMeasurementsModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentStep, setCurrentStep] = useState<ModalStep>('select-supplier');
  const [suppliers, setSuppliers] = useState<Customer[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Customer | null>(null);
  
  interface OrderItem {
    id: string;
    name: string;
    description: string;
    acquisition_cost: number;
    price: number;
    quantity: number;
    measurement_id?: string | null;
    measurement?: ProjectMeasurement | null;
  }
  
  const [items, setItems] = useState<OrderItem[]>([]);
  const [measurements, setMeasurements] = useState<ProjectMeasurement[]>([]);
  const [loadingMeasurements, setLoadingMeasurements] = useState(false);
  const [selectedMeasurementId, setSelectedMeasurementId] = useState<string | null>(null);
  const [selectedMeasurement, setSelectedMeasurement] = useState<ProjectMeasurement | null>(null);
  const [measurementPhotos, setMeasurementPhotos] = useState<Record<string, MeasurementPhoto[]>>({});
  const [measurementPresets, setMeasurementPresets] = useState<MeasurementPreset[]>([]);
  const [loadingMeasurementPresets, setLoadingMeasurementPresets] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    acquisition_cost: '',
    price: '',
    quantity: '1'
  });
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const priceWasAutoFilledRef = useRef(false);
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
  // Ref so onEmailSent (same tick as create) always sees the new PO id.
  const createdOrderIdRef = useRef<string | null>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  // Helper functions for measurement units
  const normalizeFieldKey = (key: string) => {
    return key.toLowerCase().trim().replace(/\s+/g, '_');
  };

  const findMatchingPreset = (measurement: ProjectMeasurement) => {
    if (!measurement) return null;

    const measurementFields = new Set<string>();
    if (measurement.length !== null && measurement.length !== undefined) measurementFields.add('length');
    if (measurement.width !== null && measurement.width !== undefined) measurementFields.add('width');
    if (measurement.height !== null && measurement.height !== undefined) measurementFields.add('height');
    if (measurement.area !== null && measurement.area !== undefined) measurementFields.add('area');
    if (measurement.custom_fields) {
      Object.keys(measurement.custom_fields).forEach(key => {
        measurementFields.add(normalizeFieldKey(key));
      });
    }

    for (const preset of measurementPresets) {
      if (preset.custom_fields && preset.custom_fields.length > 0) {
        const presetFieldsSet = new Set(
          preset.custom_fields.map((f: any) => normalizeFieldKey(f.name))
        );

        const presetFieldsArray = Array.from(presetFieldsSet);
        const hasAllPresetFields = presetFieldsArray.length > 0 &&
          presetFieldsArray.every(field => measurementFields.has(field));

        const measurementFieldsArray = Array.from(measurementFields);
        const hasNoExtraFields = measurementFieldsArray.every(field =>
          presetFieldsSet.has(field) || field === 'area'
        );

        if (hasAllPresetFields && hasNoExtraFields) {
          return preset;
        }
      } else {
        const presetFields = new Set<string>();
        if (preset.has_length) presetFields.add('length');
        if (preset.has_width) presetFields.add('width');
        if (preset.has_height) presetFields.add('height');
        if (preset.has_area) presetFields.add('area');

        const presetFieldsArray = Array.from(presetFields);
        const hasAllPresetFields = presetFieldsArray.length > 0 &&
          presetFieldsArray.every(field => measurementFields.has(field));

        if (hasAllPresetFields) {
          return preset;
        }
      }
    }

    return null;
  };

  const getStandardFieldUnit = (measurement: ProjectMeasurement, fieldName: string) => {
    const preset = findMatchingPreset(measurement);
    if (preset?.custom_fields && preset.custom_fields.length > 0) {
      const presetField = preset.custom_fields.find((f: any) => normalizeFieldKey(f.name) === normalizeFieldKey(fieldName));
      if (presetField?.unit) return presetField.unit;
    }
    return measurement.unit;
  };

  const getCustomFieldUnit = (measurement: ProjectMeasurement, fieldKey: string) => {
    const preset = findMatchingPreset(measurement);
    if (!preset?.custom_fields || preset.custom_fields.length === 0) return undefined;

    const normalizedKey = normalizeFieldKey(fieldKey);
    const presetField = preset.custom_fields.find((field: any) => {
      const normalizedName = normalizeFieldKey(field.name);
      const normalizedLabel = normalizeFieldKey(field.label || '');
      return normalizedName === normalizedKey || normalizedLabel === normalizedKey;
    });
    return presetField?.unit;
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setCurrentStep('select-supplier');
      setSelectedSupplier(null);
      setItems([]);
      setMeasurements([]);
      setSelectedMeasurementId(null);
      setSelectedMeasurement(null);
      setFormData({
        name: '',
        description: '',
        acquisition_cost: '',
        price: '',
        quantity: '1'
      });
      setNotes('');
      priceWasAutoFilledRef.current = false;
      setError(null);
    }
  }, [isOpen]);

  // Load suppliers when entering supplier selection step
  const loadSuppliers = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoadingSuppliers(true);
      const data = await getSuppliers(selectedBusiness.id);
      setSuppliers(data);
    } catch (err) {
      console.error('Error loading suppliers:', err);
    } finally {
      setLoadingSuppliers(false);
    }
  }, [selectedBusiness?.id]);

  useEffect(() => {
    if (currentStep === 'select-supplier' && suppliers.length === 0) {
      loadSuppliers();
    }
  }, [currentStep, suppliers.length, loadSuppliers]);

  // Load measurements when entering measurement selection step
  const loadMeasurements = useCallback(async () => {
    if (!projectId) return;
    
    try {
      setLoadingMeasurements(true);
      const data = await getProjectMeasurements(projectId);
      setMeasurements(data);
      
      // Load photos for all measurements
      const photosMap: Record<string, MeasurementPhoto[]> = {};
      
      for (const measurement of data) {
        if (measurement.id) {
          try {
            const photos = await getMeasurementPhotos(measurement.id);
            photosMap[measurement.id] = photos;
          } catch (err) {
            console.error(`Error loading photos for measurement ${measurement.id}:`, err);
            photosMap[measurement.id] = [];
          }
        }
      }
      
      setMeasurementPhotos(photosMap);
    } catch (err) {
      console.error('Error loading measurements:', err);
    } finally {
      setLoadingMeasurements(false);
    }
  }, [projectId]);

  const loadMeasurementPresets = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoadingMeasurementPresets(true);
      const presets = await getMeasurementPresets(selectedBusiness.id);
      setMeasurementPresets(presets);
    } catch (err) {
      console.error('Error loading measurement presets:', err);
    } finally {
      setLoadingMeasurementPresets(false);
    }
  }, [selectedBusiness?.id]);

  useEffect(() => {
    if (currentStep === 'select-measurements' && measurements.length === 0) {
      loadMeasurements();
      loadMeasurementPresets();
    }
  }, [currentStep, measurements.length, loadMeasurements, loadMeasurementPresets]);

  const handleSupplierSelect = (supplier: Customer) => {
    setSelectedSupplier(supplier);
    setCurrentStep('select-measurements');
  };

  const handleMeasurementSelect = (measurement: ProjectMeasurement) => {
    setSelectedMeasurementId(measurement.id || null);
    setSelectedMeasurement(measurement);
    setCurrentStep('item-details');
  };

  const handleAddItem = () => {
    if (!formData.name || !formData.quantity || !formData.acquisition_cost) {
      toast.error('Please fill in all required fields');
      return;
    }

    const newItem: OrderItem = {
      id: `temp-${Date.now()}`,
      name: formData.name,
      description: formData.description,
      acquisition_cost: parseFloat(formData.acquisition_cost) || 0,
      price: parseFloat(formData.price) || 0,
      quantity: parseInt(formData.quantity) || 1,
      measurement_id: selectedMeasurementId,
      measurement: selectedMeasurement
    };

    setItems(prev => [...prev, newItem]);
    
    // Reset form
    setFormData({
      name: '',
      description: '',
      acquisition_cost: '',
      price: '',
      quantity: '1'
    });
    priceWasAutoFilledRef.current = false;
    
    // Go back to measurement selection
    setCurrentStep('select-measurements');
    setSelectedMeasurementId(null);
    setSelectedMeasurement(null);
  };

  const handleRemoveItem = (itemId: string) => {
    setItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handlePlaceOrder = async () => {
    if (!selectedBusiness?.id || !selectedSupplier || items.length === 0) {
      toast.error('Please complete all required fields');
      return;
    }

    // Just show the order summary modal - it will handle the actual order creation
    setShowOrderSummary(true);
  };

  // Handle sending the order (called by OrderSummaryPDFModal)
  const handleSendOrder = async (orderNumber: string): Promise<boolean | string> => {
    if (!selectedBusiness?.id || !selectedSupplier || items.length === 0) {
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      // Create one-time stock items for each order item
      const orderItemsData: DBOrderItem[] = [];

      for (const item of items) {
        // Find the measurement for this item
        const measurement = measurements.find(m => m.id === item.measurement_id);

        // Build the description with measurement info
        let description = item.description || '';
        
        if (measurement) {
          const measurementParts: string[] = [];
          
          if (measurement.length) {
            const unit = getStandardFieldUnit(measurement, 'length');
            measurementParts.push(`L: ${measurement.length}${unit}`);
          }
          if (measurement.width) {
            const unit = getStandardFieldUnit(measurement, 'width');
            measurementParts.push(`W: ${measurement.width}${unit}`);
          }
          if (measurement.height) {
            const unit = getStandardFieldUnit(measurement, 'height');
            measurementParts.push(`H: ${measurement.height}${unit}`);
          }
          if (measurement.area) {
            const unit = getStandardFieldUnit(measurement, 'area');
            measurementParts.push(`Area: ${measurement.area}${unit}²`);
          }
          
          if (measurement.custom_fields && Object.keys(measurement.custom_fields).length > 0) {
            Object.entries(measurement.custom_fields).forEach(([key, value]) => {
              measurementParts.push(`${key}: ${value}`);
            });
          }
          
          if (measurementParts.length > 0) {
            description = description 
              ? `${description}\n\nMeasurement (${measurement.name}): ${measurementParts.join(' | ')}`
              : `Measurement (${measurement.name}): ${measurementParts.join(' | ')}`;
          }
        }

        // Create a one-time stock item
        const stockItem = await createStockItem({
          business_id: selectedBusiness.id,
          name: item.name,
          description: description,
          category_id: null,
          supplier_id: selectedSupplier.id,
          unit: 'unit',
          sku: null,
          price: item.price || 0,
          is_sellable: false,
          acquisition_cost: item.acquisition_cost,
          tax_rate: 0,
          quantity: 0, // One-time items start at 0
          reorder_point: null,
          max_quantity: null,
          is_one_time: true
        });

        orderItemsData.push({
          item_id: stockItem.id,
          quantity: item.quantity,
          unit_price: item.acquisition_cost,
          total_price: item.acquisition_cost * item.quantity,
          item_name: item.name,
          item_unit: 'unit',
          notes: item.measurement_id ? `Measurement ID: ${item.measurement_id}` : undefined
        });
      }

      // Calculate total
      const totalAmount = orderItemsData.reduce((sum, item) => sum + item.total_price, 0);

      // Prepare order data
      const orderData = {
        business_id: selectedBusiness.id,
        supplier_id: selectedSupplier.id,
        order_number: orderNumber,
        project_id: projectId || null,
        total_amount: totalAmount,
        notes: notes || null,
        status: 'pending'
      };

      // Create order (the PO system assigns the real number)
      const createdOrder = await createOrder(orderData, orderItemsData);
      
      setCreatedOrderId(createdOrder.id || null);
      createdOrderIdRef.current = createdOrder.id || null;
      
      toast.success('Order created successfully!');
      
      if (onOrderCreated) {
        onOrderCreated();
      }

      // Return the DB-assigned PO number so the email/SMS use it.
      return createdOrder.order_number || true;
    } catch (err) {
      console.error('Error creating order:', err);
      setError(err instanceof Error ? err.message : 'Failed to create order');
      toast.error('Failed to create order');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleBack = () => {
    if (currentStep === 'select-measurements') {
      setCurrentStep('select-supplier');
    } else if (currentStep === 'item-details') {
      setCurrentStep('select-measurements');
      setSelectedMeasurementId(null);
      setSelectedMeasurement(null);
    } else if (currentStep === 'summary') {
      setCurrentStep('select-measurements');
    }
  };

  const handleContinueToSummary = () => {
    if (items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    setCurrentStep('summary');
  };

  const handleAcquisitionCostChange = (value: string) => {
    setFormData(prev => {
      // Auto-fill price if it hasn't been manually set
      const shouldAutoFill = !priceWasAutoFilledRef.current || prev.price === prev.acquisition_cost;
      
      if (shouldAutoFill) {
        priceWasAutoFilledRef.current = true;
        return { ...prev, acquisition_cost: value, price: value };
      }
      
      return { ...prev, acquisition_cost: value };
    });
  };

  const handlePriceChange = (value: string) => {
    setFormData(prev => ({ ...prev, price: value }));
    // Mark that price was manually changed
    if (value !== formData.acquisition_cost) {
      priceWasAutoFilledRef.current = false;
    }
  };

  if (!isOpen && !isAnimating) return null;

  const renderStepContent = () => {
    switch (currentStep) {
      case 'select-supplier':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Select Supplier</h3>
              <p className="text-sm text-gray-500 mt-1">Choose the supplier for this order</p>
            </div>

            {loadingSuppliers ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
              </div>
            ) : suppliers.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No suppliers found</p>
                <p className="text-xs text-gray-400 mt-1">Add suppliers in your customer management</p>
              </div>
            ) : (
              <div className="space-y-2">
                {suppliers.map(supplier => (
                  <button
                    key={supplier.id}
                    onClick={() => handleSupplierSelect(supplier)}
                    className="w-full p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 active:bg-blue-100 transition-all duration-150 active:scale-[0.98] text-left"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-semibold text-sm flex-shrink-0">
                        {supplier.first_name?.[0] || ''}{supplier.last_name?.[0] || ''}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900">
                          {supplier.first_name} {supplier.last_name}
                        </div>
                        {supplier.email && (
                          <div className="text-xs text-gray-500 truncate">{supplier.email}</div>
                        )}
                      </div>
                      <Check className="h-5 w-5 text-gray-400" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        );

      case 'select-measurements':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Select Measurements</h3>
              <p className="text-sm text-gray-500 mt-1">Choose measurements to create order items</p>
            </div>

            {/* Items Added */}
            {items.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-gray-700">Items Added ({items.length})</div>
                  <button
                    onClick={handleContinueToSummary}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 active:bg-blue-800 transition-colors active:scale-95"
                  >
                    Continue
                  </button>
                </div>
                <div className="space-y-2">
                  {items.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        <div className="text-xs text-gray-500">
                          Qty: {item.quantity} × ${item.acquisition_cost.toFixed(2)}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveItem(item.id)}
                        className="ml-2 p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {loadingMeasurements ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
              </div>
            ) : measurements.length === 0 ? (
              <div className="text-center py-12">
                <Ruler className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No measurements found</p>
                <p className="text-xs text-gray-400 mt-1">Add measurements to the project first</p>
              </div>
            ) : (
              <div className="space-y-3">
                {measurements.map(measurement => {
                  const photos = measurementPhotos[measurement.id || ''] || [];
                  
                  return (
                    <button
                      key={measurement.id}
                      onClick={() => handleMeasurementSelect(measurement)}
                      className="w-full p-4 bg-white rounded-xl border-2 border-gray-200 hover:border-gray-300 active:scale-[0.98] transition-all duration-150 text-left"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100">
                          <Ruler className="h-5 w-5 text-gray-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 mb-1">
                            {measurement.name}
                          </div>
                          
                          {/* Standard measurement fields */}
                          {(measurement.length || measurement.width || measurement.height || measurement.area) && (
                            <div className="flex flex-wrap gap-2 mb-2 text-xs text-gray-600">
                              {measurement.length && <span>L: {measurement.length} {getStandardFieldUnit(measurement, 'length')}</span>}
                              {measurement.width && <span>W: {measurement.width} {getStandardFieldUnit(measurement, 'width')}</span>}
                              {measurement.height && <span>H: {measurement.height} {getStandardFieldUnit(measurement, 'height')}</span>}
                              {measurement.area && <span>Area: {measurement.area} {getStandardFieldUnit(measurement, 'area')}²</span>}
                            </div>
                          )}

                          {/* Custom fields */}
                          {measurement.custom_fields && Object.keys(measurement.custom_fields).length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2 text-xs text-gray-600">
                              {Object.entries(measurement.custom_fields).map(([key, value]) => {
                                const displayValue = getCustomFieldValue(value);
                                const unit = getFieldUnit(value, getCustomFieldUnit(measurement, key));
                                return (
                                  <span key={key}>
                                    {key}: {displayValue}{unit ? ` ${unit}` : ''}
                                  </span>
                                );
                              })}
                            </div>
                          )}

                          {/* Notes */}
                          {measurement.notes && (
                            <div className="text-xs text-gray-600 mt-2 pt-2 border-t border-gray-200/60">
                              <span className="font-medium">Notes: </span>
                              {measurement.notes}
                            </div>
                          )}

                          {/* Measurement Photos */}
                          {photos.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-200/60">
                              <div className="flex items-center gap-1 mb-1">
                                <ImageIcon className="h-2.5 w-2.5 text-gray-500" />
                                <span className="text-[10px] font-medium text-gray-600">
                                  Photos ({photos.length})
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                {photos.slice(0, 3).map((photo) => {
                                  const photoUrl = getMeasurementPhotoUrl(photo.file_path);
                                  return (
                                    <div
                                      key={photo.id}
                                      className="relative w-12 h-12 rounded overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0"
                                    >
                                      <img
                                        src={photoUrl}
                                        alt={photo.file_name || 'Measurement photo'}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%" y="50%" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
                                        }}
                                      />
                                    </div>
                                  );
                                })}
                                {photos.length > 3 && (
                                  <span className="text-[10px] text-gray-500 font-medium">
                                    .....
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 'item-details':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Item Details</h3>
              <p className="text-sm text-gray-500 mt-1">Enter details for the order item</p>
            </div>

            {/* Measurement Info */}
            {selectedMeasurement && (
              <div className="mb-5 pb-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Ruler className="h-4 w-4 text-blue-600" />
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">From Measurement</span>
                  </div>
                  <button
                    onClick={() => {
                      setCurrentStep('select-measurements');
                      setSelectedMeasurementId(null);
                      setSelectedMeasurement(null);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
                  >
                    Change
                  </button>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900">{selectedMeasurement.name}</div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-gray-600">
                      {selectedMeasurement.length && <span>L: {selectedMeasurement.length} {getStandardFieldUnit(selectedMeasurement, 'length')}</span>}
                      {selectedMeasurement.width && <span>W: {selectedMeasurement.width} {getStandardFieldUnit(selectedMeasurement, 'width')}</span>}
                      {selectedMeasurement.height && <span>H: {selectedMeasurement.height} {getStandardFieldUnit(selectedMeasurement, 'height')}</span>}
                      {selectedMeasurement.area && <span>Area: {selectedMeasurement.area} {getStandardFieldUnit(selectedMeasurement, 'area')}²</span>}
                      {selectedMeasurement.custom_fields && Object.keys(selectedMeasurement.custom_fields).length > 0 && (
                        <>
                          {Object.entries(selectedMeasurement.custom_fields).map(([key, value]) => (
                            <span key={key}>
                              {key}: {value}{getCustomFieldUnit(selectedMeasurement, key) ? ` ${getCustomFieldUnit(selectedMeasurement, key)}` : ''}
                            </span>
                          ))}
                        </>
                      )}
                    </div>
                    {selectedMeasurement.notes && (
                      <div className="mt-2 text-xs text-gray-500 italic">{selectedMeasurement.notes}</div>
                    )}
                    {/* Measurement Photos */}
                    {measurementPhotos[selectedMeasurement.id || ''] && measurementPhotos[selectedMeasurement.id || ''].length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200/60">
                        <div className="flex items-center gap-1 mb-1">
                          <ImageIcon className="h-2.5 w-2.5 text-gray-500" />
                          <span className="text-[10px] font-medium text-gray-600">
                            Photos ({measurementPhotos[selectedMeasurement.id || ''].length})
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {measurementPhotos[selectedMeasurement.id || ''].slice(0, 3).map((photo) => {
                            const photoUrl = getMeasurementPhotoUrl(photo.file_path);
                            return (
                              <div
                                key={photo.id}
                                className="relative w-12 h-12 rounded overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0"
                              >
                                <img
                                  src={photoUrl}
                                  alt={photo.file_name || 'Measurement photo'}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%" y="50%" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
                                  }}
                                />
                              </div>
                            );
                          })}
                          {measurementPhotos[selectedMeasurement.id || ''].length > 3 && (
                            <span className="text-[10px] text-gray-500 font-medium">
                              .....
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Form Fields */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Item Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter item name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={3}
                  placeholder="Enter item description"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Acquisition Cost *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      value={formData.acquisition_cost}
                      onChange={(e) => handleAcquisitionCostChange(e.target.value)}
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Retail Price
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => handlePriceChange(e.target.value)}
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity *
                </label>
                <input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="1"
                  min="1"
                />
              </div>
            </div>

            {/* Add Item Button */}
            <button
              onClick={handleAddItem}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 active:bg-blue-800 transition-colors active:scale-[0.98]"
            >
              <Plus className="h-4 w-4 inline mr-2" />
              Add Item
            </button>
          </div>
        );

      case 'summary':
        const totalAmount = items.reduce((sum, item) => sum + (item.acquisition_cost * item.quantity), 0);
        
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Order Summary</h3>
              <p className="text-sm text-gray-500 mt-1">Review and place your order</p>
            </div>

            {/* Supplier Info */}
            {selectedSupplier && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Supplier</div>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-semibold text-sm">
                    {selectedSupplier.first_name?.[0] || ''}{selectedSupplier.last_name?.[0] || ''}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">
                      {selectedSupplier.first_name} {selectedSupplier.last_name}
                    </div>
                    {selectedSupplier.email && (
                      <div className="text-xs text-gray-500">{selectedSupplier.email}</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Items */}
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Items ({items.length})</div>
              <div className="space-y-2">
                {items.map(item => (
                  <div key={item.id} className="p-3 bg-white border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{item.name}</div>
                        {item.description && (
                          <div className="text-xs text-gray-500 mt-1">{item.description}</div>
                        )}
                        {item.measurement && (
                          <div className="flex items-center gap-1 mt-1">
                            <Ruler className="h-3 w-3 text-blue-500" />
                            <span className="text-xs text-blue-600">
                              {measurementPresets.find(p => p.id === item.measurement?.preset_id)?.name || 'Measurement'}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="text-right ml-3">
                        <div className="text-sm font-semibold text-gray-900">
                          ${(item.acquisition_cost * item.quantity).toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.quantity} × ${item.acquisition_cost.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                rows={3}
                placeholder="Add any additional notes for this order..."
              />
            </div>

            {/* Total */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">Total Amount</span>
                <span className="text-xl font-bold text-blue-600">${totalAmount.toFixed(2)}</span>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Place Order Button */}
            <button
              onClick={handlePlaceOrder}
              disabled={loading}
              className="w-full py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 active:bg-green-800 transition-colors active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Placing Order...
                </span>
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4 inline mr-2" />
                  Place Order
                </>
              )}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {createPortal(
        <div
          className={`fixed inset-0 transition-opacity duration-200 ${
            isOpen ? 'bg-black/50' : 'bg-black/0'
          }`}
          style={{
            zIndex: 100070,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
            paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
          }}
          onClick={onClose}
          onTransitionEnd={handleTransitionEnd}
        >
          <div
            className={`bg-white transform transition-all duration-200 ${
              isMobile
                ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
                : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                    isOpen ? 'translate-x-0' : 'translate-x-full'
                  }`
            }`}
            style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
              zIndex: 100080
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col overflow-hidden">
              {/* Header */}
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                {currentStep !== 'select-supplier' && (
                  <button
                    onClick={handleBack}
                    className="flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Back
                  </button>
                )}
                <h2 className={`text-xl font-bold text-gray-900 ${currentStep === 'select-supplier' ? '' : 'ml-auto'}`}>
                  Place Order from Measurements
                </h2>
                <button
                  onClick={onClose}
                  className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-all"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {renderStepContent()}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Order Summary PDF Modal */}
      {showOrderSummary && selectedSupplier && (
        <OrderSummaryPDFModal
          isOpen={showOrderSummary}
          onClose={() => {
            setShowOrderSummary(false);
            onClose();
          }}
          supplier={selectedSupplier}
          items={items.map(item => {
            // Find the measurement for this item
            const measurement = measurements.find(m => m.id === item.measurement_id);
            const photos = measurementPhotos[item.measurement_id || ''] || [];
            
            return {
              item: {
                id: item.id,
                item_name: item.name,
                description: item.description,
                acquisition_cost: item.acquisition_cost,
                selling_price: item.price,
                low_stock_threshold: 0,
                business_id: selectedBusiness?.id || '',
                preferred_supplier_id: selectedSupplier.id,
                current_stock: 0,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                unit: 'unit'
              } as any,
              quantityToOrder: item.quantity,
              totalCost: item.acquisition_cost * item.quantity,
              measurement: measurement ? {
                name: measurement.name,
                length: measurement.length || undefined,
                width: measurement.width || undefined,
                height: measurement.height || undefined,
                area: measurement.area || undefined,
                unit: getStandardFieldUnit(measurement, 'length'),
                custom_fields: measurement.custom_fields || undefined,
                notes: measurement.notes || undefined,
                photos: photos.map(photo => ({
                  url: getMeasurementPhotoUrl(photo.file_path)
                }))
              } : null
            };
          })}
          totalAmount={items.reduce((sum, item) => sum + (item.acquisition_cost * item.quantity), 0)}
          onSendOrder={handleSendOrder}
          onEmailSent={async (orderNumber, messageId, threadId, emailBody) => {
            const poId = createdOrderIdRef.current || createdOrderId;
            if (!selectedBusiness?.id || !poId || !selectedSupplier?.email) return;
            try {
              await updateOrderEmailInfo(poId, messageId, threadId);
              await linkEmailToOrder(
                poId,
                selectedBusiness.id,
                selectedSupplier.id,
                selectedSupplier.email,
                `[ORDER-${orderNumber}] ${orderNumber}`,
                emailBody,
                messageId
              );
            } catch (err) {
              console.error('Error linking measurement PO email:', err);
            }
          }}
        />
      )}
    </>
  );
}

